import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export type EmailProvider = 'gmail' | 'outlook' | 'gmail_imap';

export const ALL_PROVIDERS: EmailProvider[] = ['gmail', 'outlook', 'gmail_imap'];

const OAUTH_STORAGE_PREFIXES = [
  'oauth_access_token_',
  'oauth_success_',
  'oauth_timestamp_',
  'oauth_response_',
];

export interface ProviderTokens {
  gmail?: string;
  outlook?: string;
  gmail_imap?: string;
}

export interface PasswordAuthPayload {
  provider: 'gmail_imap';
  email: string;
  password: string;
}

export interface PaginatedEmailResponse {
  emails: any[];
  nextPageToken?: string;
  hasMore: boolean;
}

export interface Attachment {
  Filename: string;
  ContentType: string;
  Data: string; // Base64 encoded
  Size: number;
}

export interface Email {
  ID: string;
  ProviderID: string;
  From: string;
  To: string[];
  Cc: string[];
  Bcc: string[];
  Subject: string;
  Body: string;
  HTMLBody: string;
  Attachments: Attachment[];
  Date: string;
  Read: boolean;
  Favorite?: boolean;
  Labels: string[];
  ThreadID: string;
  DraftID: string;
  HasAttachments: boolean;
  // Set when the backend shortened the body; the full text still needs `/content`.
  BodyTruncated?: boolean;
  MessageID?: string;
  InReplyTo?: string;
  References?: string;
}

export interface ThreadOthersResponse {
  threadId: string;
  messages: Email[];
}

export interface EmailThread {
  ThreadID: string;
  Messages: Email[];
  Latest: Email;
  Subject: string;
  Participants: string[];
  LatestDate: string;
  UnreadCount: number;
  MessageCount: number;
  HasAttachments: boolean;
}

export interface EmailLabel {
  id: string;
  name: string;
  type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailOAuthService {
  private http: HttpClient = inject(HttpClient);
  private readonly baseUrl = environment.emailSyncUrl;
  private providerTokens: ProviderTokens = {};

  // Signals for stateful email management
  private _emails = signal<Email[]>([]);
  private _nextPageToken = signal<string | null>(null);
  private _hasMore = signal<boolean>(false);
  private _isLoading = signal<boolean>(false);
  private _error = signal<string | null>(null);
  private _currentProvider = signal<EmailProvider | null>(null);
  private _currentLabels = signal<string | null>(null);
  private _currentPageToken = signal<string | null>(null);

  // Signal for currently viewed email
  private _viewedEmail = signal<Email | null>(null);
  private _viewedEmailId = signal<string | null>(null);
  private _isLoadingEmailContent = signal<boolean>(false);
  private _emailContentError = signal<string | null>(null);
  private _isLoadingAttachments = signal<boolean>(false);
  private _attachmentsError = signal<string | null>(null);

  // Signal for OAuth flow status
  private _isOAuthInProgress = signal<boolean>(false);
  private _oauthProvider = signal<EmailProvider | null>(null);

  // Signal for connected providers response
  private _connectedProvidersData = signal<any>(null);

  // Signal for current active account
  private _currentAccount = signal<string | null>(null);

  // Public computed signals
  emails = computed(() => this._emails());
  nextPageToken = computed(() => this._nextPageToken());
  hasMore = computed(() => this._hasMore());
  isLoading = computed(() => this._isLoading());
  error = computed(() => this._error());
  currentProvider = computed(() => this._currentProvider());
  hasEmails = computed(() => this._emails().length > 0);
  connectedProvidersData = computed(() => this._connectedProvidersData());
  currentAccount = computed(() => this._currentAccount());

  setCurrentAccount(account: string | null): void {
    this._currentAccount.set(account);
  }

  // Public computed signals for viewed email
  viewedEmail = computed(() => this._viewedEmail());
  viewedEmailId = computed(() => this._viewedEmailId());
  isLoadingEmailContent = computed(() => this._isLoadingEmailContent());
  emailContentError = computed(() => this._emailContentError());
  isLoadingAttachments = computed(() => this._isLoadingAttachments());
  attachmentsError = computed(() => this._attachmentsError());

  // Public computed signals for OAuth flow
  isOAuthInProgress = computed(() => this._isOAuthInProgress());
  oauthProvider = computed(() => this._oauthProvider());

  constructor() {
    // Load tokens from localStorage on service initialization
    this.loadTokensFromStorage();
    // Check if OAuth is in progress
    this.checkOAuthStatus();
  }

  getAccounts(): Observable<any> {
    let accounts = [];

    if (localStorage.getItem('oauth_access_token_gmail')) {
      const decodedGmailToken = jwtDecode(
        localStorage.getItem('oauth_access_token_gmail') as string
      );
      //const email = decodedGmailToken.email;

      accounts.push({
        name: 'Gmail',
        provider: 'gmail',
      });
    }

    if (localStorage.getItem('oauth_access_token_outlook')) {
      const decodedOutlookToken: { upn: string; name: string } = jwtDecode(
        localStorage.getItem('oauth_access_token_outlook') as string
      ) as any;
      //const email = decodedGmailToken.email;
      accounts.push({
        name: decodedOutlookToken.name,
        provider: 'outlook',
        email: decodedOutlookToken.upn,
      });
    }

    return new Observable(observer => {
      observer.next(accounts);
      observer.complete();
    });
  }

  private loadTokensFromStorage(): void {
    const gmailToken = localStorage.getItem('oauth_access_token_gmail');
    const outlookToken = localStorage.getItem('oauth_access_token_outlook');

    this.providerTokens = {
      gmail: gmailToken || undefined,
      outlook: outlookToken || undefined,
    };
  }

  private checkOAuthStatus(): void {
    const oauthInProgress = localStorage.getItem('oauth_in_progress');
    const oauthProvider = localStorage.getItem('oauth_provider');

    if (oauthInProgress === 'true' && oauthProvider) {
      this._isOAuthInProgress.set(true);
      this._oauthProvider.set(oauthProvider as EmailProvider);
    }
  }

  private setOAuthInProgress(provider: EmailProvider): void {
    this._isOAuthInProgress.set(true);
    this._oauthProvider.set(provider);
    localStorage.setItem('oauth_in_progress', 'true');
    localStorage.setItem('oauth_provider', provider);
  }

  private clearOAuthInProgress(): void {
    this._isOAuthInProgress.set(false);
    this._oauthProvider.set(null);
    localStorage.removeItem('oauth_in_progress');
    localStorage.removeItem('oauth_provider');
  }

  private clearProviderStorage(provider?: EmailProvider): void {
    if (provider) {
      delete this.providerTokens[provider];
      for (const prefix of OAUTH_STORAGE_PREFIXES) {
        localStorage.removeItem(`${prefix}${provider}`);
      }
    } else {
      this.providerTokens = {};
      for (const p of ALL_PROVIDERS) {
        for (const prefix of OAUTH_STORAGE_PREFIXES) {
          localStorage.removeItem(`${prefix}${p}`);
        }
      }
      // Also remove legacy tokens for backward compatibility
      localStorage.removeItem('oauth_access_token');
      localStorage.removeItem('oauth_success');
      localStorage.removeItem('oauth_timestamp');
      localStorage.removeItem('oauth_response');
    }
  }

  async startOAuthFlow(provider: EmailProvider = 'gmail'): Promise<void> {
    const payload = {
      provider: provider,
    };

    try {
      // Set OAuth in progress before redirecting
      this.setOAuthInProgress(provider);

      const response = await firstValueFrom(
        this.http.post<any>(`${this.baseUrl}/oauth/authorize`, payload, {
          observe: 'response',
        })
      );

      const responseBody = response.body;

      if (!responseBody || !responseBody.authorizationUrl) {
        this.clearOAuthInProgress();
        throw new Error('Invalid response from server - no authorization URL received');
      }

      // Redirect to the authorization URL
      window.location.href = responseBody.authorizationUrl;
    } catch (error) {
      this.clearOAuthInProgress();
      throw error;
    }
  }

  async handleOAuthCallback(
    code: string,
    state: string,
    provider: EmailProvider = 'gmail'
  ): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.baseUrl}/oauth/${provider}/callback`, {
          params: {
            code: code,
            state: state,
          },
          observe: 'response',
        })
      );

      // Store the access token for the specific provider
      if (response.body && response.body.accessToken) {
        const accessToken = response.body.accessToken;
        this.providerTokens[provider] = accessToken;
        //localStorage.setItem(`oauth_access_token_${provider}`, accessToken);
      }

      // Clear OAuth in progress status
      this.clearOAuthInProgress();

      return response.body;
    } catch (error) {
      this.clearOAuthInProgress();
      throw error;
    }
  }

  connectWithPassword(payload: PasswordAuthPayload): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/oauth/password`, payload, {
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        tap(() => {
          this.clearOAuthInProgress();
        }),
        switchMap(responseData => this.readUserConnectedProviders().pipe(map(() => responseData))),
        catchError(error => {
          this.clearOAuthInProgress();
          return throwError(() => error);
        })
      );
  }

  getEmails(
    provider: EmailProvider,
    labels?: string,
    pageToken?: string,
    query?: string,
    mailbox?: string
  ): Observable<PaginatedEmailResponse> {
    const token = this.providerTokens[provider];
    /*if (!token) {
      this._error.set(`No access token available for ${provider}. Please authenticate first.`);
      return throwError(
        () => new Error(`No access token available for ${provider}. Please authenticate first.`)
      );
    }*/

    // Update state signals
    this._isLoading.set(true);
    this._error.set(null);
    this._currentProvider.set(provider);
    this._currentLabels.set(labels || null);
    this._currentPageToken.set(pageToken || null);

    const params: any = {
      provider: provider,
      groupByThreads: 'true',
    };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (labels) {
      params.labels = labels;
    }

    if (pageToken) {
      params.pageToken = pageToken;
    }

    if (query) {
      params.query = query;
    }

    if (mailbox) {
      params.mailbox = mailbox;
    }

    return this.http
      .get<any>(`${this.baseUrl}/emails/paginated`, {
        params: params,
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body;
        }),
        tap((emailResponse: any) => {
          const emailsList = this.parseEmailsFromResponse(emailResponse);

          // Update state signals on success
          this._emails.set(emailsList);
          this._nextPageToken.set(emailResponse?.nextPageToken || null);
          this._hasMore.set(Boolean(emailResponse?.hasMore));
          this._isLoading.set(false);
          this._error.set(null);
        }),
        catchError(error => {
          // Update state signals on error
          this._isLoading.set(false);
          this._error.set(error.message || 'Failed to fetch emails');
          return throwError(() => error);
        })
      );
  }

  getEmailContent(
    provider: EmailProvider,
    emailId: string,
    includeAttachments: boolean = true,
    mailbox?: string
  ): Observable<Email> {
    const token = this.providerTokens[provider];
    /*if (!token) {
      this._emailContentError.set(
        `No access token available for ${provider}. Please authenticate first.`
      );
      return throwError(
        () => new Error(`No access token available for ${provider}. Please authenticate first.`)
      );
    }*/

    // Update state signals
    this._isLoadingEmailContent.set(true);
    this._emailContentError.set(null);
    this._viewedEmailId.set(emailId);

    const params: any = {
      provider: provider,
    };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (!includeAttachments) {
      params.includeAttachments = 'false';
    }

    if (mailbox) {
      params.mailbox = mailbox;
    }
    return this.http
      .get<Email>(`${this.baseUrl}/emails/${emailId}/content`, {
        params: params,
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body!;
        }),
        tap(email => {
          // Update state signals on success
          this._viewedEmail.set(email);
          // Keep ID in sync if present on payload
          const payloadId = (email as any)?.ID || (email as any)?.ProviderID || null;
          if (payloadId) {
            this._viewedEmailId.set(payloadId);
          }
          // TEMP: expose to window so attachment component can read emailId for BE upload
          try {
            (window as any).currentViewedEmail = email;
          } catch {}
          this._isLoadingEmailContent.set(false);
          this._emailContentError.set(null);
        }),
        catchError(error => {
          // Update state signals on error
          this._isLoadingEmailContent.set(false);
          this._emailContentError.set(error.message || 'Failed to fetch email content');
          this._viewedEmail.set(null);
          return throwError(() => error);
        })
      );
  }

  /**
   * Fetch the messages of the thread an email belongs to, with their bodies filled in.
   * GET /emails/{emailId}/thread/others — addressed by message id, not thread id.
   * Stays out of the `_viewedEmail` / loading signals: it enriches the thread in the
   * background while the selected message renders from its own `/content` call.
   */
  getThreadOthers(
    provider: EmailProvider,
    emailId: string,
    mailbox?: string
  ): Observable<ThreadOthersResponse> {
    if (!emailId) return of({ threadId: '', messages: [] });

    const params: any = {
      provider: provider,
    };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }

    return this.http
      .get<ThreadOthersResponse>(`${this.baseUrl}/emails/${emailId}/thread/others`, { params })
      .pipe(
        map(response => {
          const messages = (response?.messages ?? []).filter(Boolean);
          return {
            threadId: response?.threadId || messages[0]?.ThreadID || '',
            messages,
          };
        })
      );
  }

  /**
   * Fetch attachments for an email separately (lazy loading)
   * Uses the content endpoint with includeAttachments=true to get attachments
   */
  getEmailAttachments(
    provider: EmailProvider,
    emailId: string,
    mailbox?: string
  ): Observable<Attachment[]> {
    // Update loading state
    this._isLoadingAttachments.set(true);
    this._attachmentsError.set(null);

    const params: any = {
      provider: provider,
      includeAttachments: 'true',
    };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }

    // Call content endpoint directly to avoid triggering email content loading state
    return this.http
      .get<Email>(`${this.baseUrl}/emails/${emailId}/content`, {
        params: params,
        observe: 'response',
      })
      .pipe(
        map(response => {
          const email = response.body!;
          // Extract attachments from the email response
          return email?.Attachments || [];
        }),
        tap(attachments => {
          // Update the viewed email with attachments (merge, don't replace)
          const currentEmail = this._viewedEmail();
          if (currentEmail && currentEmail.ID === emailId) {
            this._viewedEmail.set({
              ...currentEmail,
              Attachments: attachments,
            });
            // Update window reference
            try {
              (window as any).currentViewedEmail = {
                ...currentEmail,
                Attachments: attachments,
              };
            } catch {}
          }
          this._isLoadingAttachments.set(false);
          this._attachmentsError.set(null);
        }),
        catchError(error => {
          this._isLoadingAttachments.set(false);
          this._attachmentsError.set(error.message || 'Failed to fetch email attachments');
          return throwError(() => error);
        })
      );
  }

  isAuthenticated(provider?: EmailProvider): boolean {
    const connectedData = this._connectedProvidersData();

    // Check if we have connected providers data from the API
    if (connectedData?.tokens && Array.isArray(connectedData.tokens)) {
      if (provider) {
        // Check if the specific provider exists in the tokens array
        return connectedData.tokens.some((token: any) => token.provider === provider);
      }
      // Return true if any provider is connected
      return connectedData.tokens.length > 0;
    }

    // Fallback to legacy localStorage-based check
    if (provider) {
      return !!this.providerTokens[provider];
    }
    // Return true if any provider is authenticated via localStorage
    return !!(this.providerTokens.gmail || this.providerTokens.outlook);
  }

  getAuthenticatedProviders(): EmailProvider[] {
    const providers: EmailProvider[] = [];
    if (this.providerTokens.gmail) providers.push('gmail');
    if (this.providerTokens.outlook) providers.push('outlook');
    return providers;
  }

  reloadTokens(): void {
    this.loadTokensFromStorage();
  }

  sendEmail(provider: EmailProvider, emailData: any, mailbox?: string): Observable<any> {
    const params: any = { provider };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }

    return this.http
      .post<any>(`${this.baseUrl}/emails/send`, emailData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body;
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  replyEmail(
    provider: EmailProvider,
    emailId: string,
    emailData: any,
    mailbox?: string
  ): Observable<any> {
    const params: any = { provider };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }

    return this.http
      .post<any>(`${this.baseUrl}/emails/${emailId}/reply`, emailData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body;
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  replyToAllEmail(
    provider: EmailProvider,
    emailId: string,
    replyData: any,
    mailbox?: string
  ): Observable<any> {
    const params: any = { provider };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) params.mailbox = mailbox;

    return this.http
      .post<any>(`${this.baseUrl}/emails/${emailId}/reply-all`, replyData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        catchError(error => throwError(() => error))
      );
  }

  forwardEmail(
    provider: EmailProvider,
    emailId: string,
    forwardData: any,
    mailbox?: string
  ): Observable<any> {
    const params: any = { provider };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) params.mailbox = mailbox;

    return this.http
      .post<any>(`${this.baseUrl}/emails/${emailId}/forward`, forwardData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        catchError(error => throwError(() => error))
      );
  }

  logout(provider?: EmailProvider, account?: string): Observable<any> {
    const params: any = {};
    if (provider) params.provider = provider;
    if (account) params.account = account;

    return this.http
      .post<any>(`${this.baseUrl}/oauth/logout`, null, {
        params: params,
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        tap(() => {
          this.clearProviderStorage(provider);
          this.clearEmailState();
        }),
        catchError(error => {
          this.clearProviderStorage(provider);
          this.clearEmailState();
          return throwError(() => error);
        })
      );
  }

  /**
   * Parse various API response shapes into a list of EmailThread objects.
   * Handles: { emails: [] }, { threads: [{ threadId, messages }] }, { rows: [{ threadId, messages }] }, { rows: [...flat] }
   * Single-message responses are wrapped as single-message threads.
   */
  parseThreadsFromResponse(response: any): EmailThread[] {
    const raw: { threadId: string; messages: Email[] }[] = [];

    if (response?.threads && Array.isArray(response.threads)) {
      for (const t of response.threads) {
        const messages: Email[] = Array.isArray(t?.messages) ? t.messages.filter(Boolean) : [];
        if (!messages.length) continue;
        raw.push({
          threadId: t?.threadId || messages[0]?.ThreadID || messages[0]?.ID || '',
          messages,
        });
      }
    } else if (response?.rows && Array.isArray(response.rows)) {
      const looksGrouped =
        response.rows.length > 0 &&
        response.rows[0]?.messages &&
        Array.isArray(response.rows[0].messages);

      if (looksGrouped) {
        for (const row of response.rows) {
          const messages: Email[] = Array.isArray(row?.messages)
            ? row.messages.filter(Boolean)
            : [];
          if (!messages.length) continue;
          raw.push({
            threadId: row?.threadId || messages[0]?.ThreadID || messages[0]?.ID || '',
            messages,
          });
        }
      } else {
        for (const m of response.rows.filter(Boolean) as Email[]) {
          raw.push({ threadId: m.ThreadID || m.ID, messages: [m] });
        }
      }
    } else if (response?.emails && Array.isArray(response.emails)) {
      for (const m of response.emails.filter(Boolean) as Email[]) {
        raw.push({ threadId: m.ThreadID || m.ID, messages: [m] });
      }
    }

    return raw
      .map(({ threadId, messages }) => this.buildThread(threadId, messages))
      .sort((a, b) => new Date(b.LatestDate).getTime() - new Date(a.LatestDate).getTime());
  }

  /**
   * Backwards-compat helper: returns the latest message of each thread.
   * Used by callers that operate on a flat list (e.g. dedup by ID, list previews).
   */
  parseEmailsFromResponse(response: any): Email[] {
    return this.parseThreadsFromResponse(response).map(t => t.Latest);
  }

  private buildThread(threadId: string, messages: Email[]): EmailThread {
    const sorted = [...messages].sort(
      (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()
    );
    const latest = sorted[sorted.length - 1];
    const participants = Array.from(
      new Set(sorted.map(m => m.From).filter((v): v is string => !!v))
    );
    const unreadCount = sorted.filter(m => !m.Read).length;
    const hasAttachments = sorted.some(m => (m.Attachments?.length || 0) > 0);

    return {
      ThreadID: threadId || latest.ThreadID || latest.ID,
      Messages: sorted,
      Latest: latest,
      Subject: latest.Subject || sorted[0].Subject || '',
      Participants: participants,
      LatestDate: latest.Date,
      UnreadCount: unreadCount,
      MessageCount: sorted.length,
      HasAttachments: hasAttachments,
    };
  }

  // State management methods
  clearEmailState(): void {
    this._emails.set([]);
    this._nextPageToken.set(null);
    this._hasMore.set(false);
    this._isLoading.set(false);
    this._error.set(null);
    this._currentProvider.set(null);
    this._currentLabels.set(null);
    this._currentPageToken.set(null);
    this.clearViewedEmailState();
  }

  clearViewedEmailState(): void {
    this._viewedEmail.set(null);
    this._isLoadingEmailContent.set(false);
    this._emailContentError.set(null);
  }

  setViewedEmail(email: Email | null): void {
    this._viewedEmail.set(email);
    this._emailContentError.set(null);
  }

  setCurrentProvider(provider: EmailProvider): void {
    this._currentProvider.set(provider);
  }

  refreshEmails(): Observable<PaginatedEmailResponse> | null {
    const provider = this._currentProvider();
    if (!provider) {
      return null;
    }

    return this.getEmails(
      provider,
      this._currentLabels() || undefined,
      this._currentPageToken() || undefined
    );
  }

  loadMoreEmails(): Observable<PaginatedEmailResponse> | null {
    const provider = this._currentProvider();
    const nextPageToken = this._nextPageToken();

    if (!provider || !nextPageToken) {
      return null;
    }

    // Set loading state
    this._isLoading.set(true);
    this._error.set(null);

    const params: any = {
      provider: provider,
    };

    const account = this._currentAccount();
    if (account) params.account = account;

    const currentLabels = this._currentLabels();
    if (currentLabels) {
      params.labels = currentLabels;
    }

    params.pageToken = nextPageToken;

    // Make direct HTTP call without interfering with getEmails method
    return this.http
      .get<PaginatedEmailResponse>(`${this.baseUrl}/emails/paginated`, {
        params: params,
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body!;
        }),
        tap(emailResponse => {
          // Append new emails to existing ones for pagination
          const currentEmails = this._emails();
          const newEmails = emailResponse.emails || [];
          this._emails.set([...currentEmails, ...newEmails]);
          this._nextPageToken.set(emailResponse.nextPageToken || null);
          this._hasMore.set(emailResponse.hasMore || false);
          this._isLoading.set(false);
          this._error.set(null);
        }),
        catchError(error => {
          this._isLoading.set(false);
          this._error.set(error.message || 'Failed to load more emails');
          return throwError(() => error);
        })
      );
  }

  getLabels(provider: EmailProvider, mailbox?: string): Observable<EmailLabel[]> {
    const token = this.providerTokens[provider];
    /*if (!token) {
      return throwError(
        () => new Error(`No access token available for ${provider}. Please authenticate first.`)
      );
    }*/

    // if (provider === 'outlook') {
    //   return of([
    //     {
    //       id: 'sent',
    //       name: 'Sent',
    //     },
    //     {
    //       id: 'drafts',
    //       name: 'Drafts',
    //     },
    //     {
    //       id: 'deleted',
    //       name: 'Deleted',
    //     },
    //     {
    //       id: 'junk',
    //       name: 'Junk',
    //     },
    //   ]);
    // }

    const params: any = { provider };

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }
    return this.http
      .get<any>(`${this.baseUrl}/emails/labels`, {
        params,
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body;
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  readUserConnectedProviders(): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/oauth/providers`, {
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response.body;
        }),
        tap(responseData => {
          // Store the response data in signal
          this._connectedProvidersData.set(responseData);

          // If current provider is not set and we have at least one provider in tokens array
          if (
            this._currentProvider() === null &&
            responseData?.tokens &&
            Array.isArray(responseData.tokens) &&
            responseData.tokens.length > 0
          ) {
            // Set current provider to the first provider's Provider value
            const firstProvider = responseData.tokens[0].provider as EmailProvider;
            this._currentProvider.set(firstProvider);
          }
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  updateEmailMessageFlags(
    emailId: string,
    flags: { [key: string]: any },
    mailbox?: string
  ): Observable<any> {
    const provider = this._currentProvider();

    // Don't send request if provider is null
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }

    const params: any = {};

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }

    return this.http.patch<any>(`${this.baseUrl}/emails/${provider}/${emailId}/flags`, flags, {
      observe: 'response',
      params,
    });
  }

  deleteEmail(provider: EmailProvider, emailId: string, mailbox?: string): Observable<any> {
    const params: any = {};

    const account = this._currentAccount();
    if (account) params.account = account;

    if (mailbox) {
      params.mailbox = mailbox;
    }
    return this.http.delete<any>(`${this.baseUrl}/emails/${provider}/${emailId}`, {
      observe: 'response',
      params,
    });
  }

  createDraft(
    provider: EmailProvider,
    emailData: any,
    account?: string,
    mailbox?: string
  ): Observable<any> {
    const params: any = { provider };

    if (account) params.account = account;

    if (mailbox) params.mailbox = mailbox;

    return this.http
      .post<any>(`${this.baseUrl}/emails/drafts`, emailData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        catchError(error => throwError(() => error))
      );
  }

  updateDraft(
    draftId: string,
    provider: EmailProvider,
    emailData: any,
    account?: string,
    mailbox?: string
  ): Observable<any> {
    const params: any = { provider };

    if (account) params.account = account;

    if (mailbox) params.mailbox = mailbox;

    return this.http
      .patch<any>(`${this.baseUrl}/emails/drafts/${draftId}`, emailData, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        map(response => response.body),
        catchError(error => throwError(() => error))
      );
  }
}
